import { Icon, Prompt, Tooltip, SidebarHeader } from '@anytag/shared-atoms';
import { cn } from '@anytag/shared-utils';
import { useInfluencerSearchPathnames, useQueryHelper, useToggleState } from '@anytag/shared-hooks';
import { ChatRoom } from './chat-room';
import { useInitAIAssistant } from './hook';
import { ChatHistory } from './chat-history';

export const AIAssistant = () => {
  const warningModal = useToggleState();
  const { t, navigate } = useQueryHelper();
  const initialStates = useInitAIAssistant();
  const { supportedInfluencerSearchPaths } = useInfluencerSearchPathnames();

  const {
    threadId,
    isAiSidebarOpen,
    closeChat,
    refreshChat,
    isChatHistoryOpen,
    setAiAssistantState,
    isSendingMessage,
    setChatHistoryThreadId,
    isSupportedChatAIPaths,
    resetCurrentSearchParams
  } = initialStates;

  return (
    <>
      <SidebarHeader className="flex flex-row items-center justify-between gap-2 border-b-border">
        <div className="flex flex-2 justify-start gap-2">
          {isChatHistoryOpen ? (
            <Tooltip help={t('Tooltip.BackToChat')}>
              <div>
                <Icon
                  size={12}
                  icon="caret-left"
                  variant="gray"
                  onClick={() => setAiAssistantState({ isChatHistoryOpen: false })}
                  className="cursor-pointer hover:icon-color-black"
                />
              </div>
            </Tooltip>
          ) : isSupportedChatAIPaths ? (
            <>
              <Tooltip help={t('Tooltip.RefreshChat')} disabled={isSendingMessage}>
                <div>
                  <Icon
                    size={14}
                    icon="reset"
                    variant="gray"
                    className={cn(
                      'hover:icon-color-black',
                      isSendingMessage ? 'pointer-events-none' : 'cursor-pointer'
                    )}
                    onClick={() => {
                      resetCurrentSearchParams();
                      refreshChat();
                    }}
                  />
                </div>
              </Tooltip>

              <Tooltip help={t('Tooltip.ChatHistory')}>
                <div>
                  <Icon
                    size={16}
                    icon="clock"
                    variant="gray"
                    onClick={() => setAiAssistantState({ isChatHistoryOpen: true })}
                    className="cursor-pointer hover:icon-color-black"
                  />
                </div>
              </Tooltip>
            </>
          ) : null}
        </div>

        <h3 className="flex-1 text-base font-semibold whitespace-nowrap">
          {t(isChatHistoryOpen ? 'History' : 'AI Assistant')}
        </h3>

        <div className="flex flex-2 justify-end">
          {!isChatHistoryOpen ? (
            <Tooltip help={t('Tooltip.Close')}>
              <div>
                <Icon
                  icon="close"
                  size={12}
                  variant="gray"
                  className="cursor-pointer hover:icon-color-black"
                  onClick={isSupportedChatAIPaths && !!threadId ? warningModal.open : closeChat}
                />
              </div>
            </Tooltip>
          ) : null}
        </div>
      </SidebarHeader>

      {isChatHistoryOpen ? (
        <ChatHistory
          isAiSidebarOpen={isAiSidebarOpen}
          threadId={initialStates.threadId}
          isChatHistoryOpen={isChatHistoryOpen}
          setAiAssistantState={setAiAssistantState}
          setChatHistoryThreadId={setChatHistoryThreadId}
        />
      ) : (
        <ChatRoom {...initialStates} />
      )}

      <Prompt
        open={warningModal.status} // Open prompt manually
        title={t('Are you sure to close the chat')}
        description={t('It will clear all AI filters and chat history')}
        when={!!isAiSidebarOpen && !!threadId} // Open prompt automatically when leave the page
        ignorePaths={supportedInfluencerSearchPaths} // We can switch among influencers search SNS without showing this prompt
        footer={({ next, reset }) => ({
          cancelButton: { onClick: warningModal.close },
          submitButton: {
            title: t<string>('Button.Sure'),
            onClick: () => {
              warningModal.close();
              closeChat(); // Close chat box - delete chat thread

              // Case: Close chat box and stay at current page
              if (!next) {
                resetCurrentSearchParams();
              } else {
                // Case: Leave the page
                // Custom Prompt location to reset all search params if navigate to another tab while using chatbox
                // https://adasiaholdings.atlassian.net/browse/AT-5990
                reset();
                setTimeout(() => {
                  navigate({ to: next.pathname });
                }, 0); // wait for clear threadId
              }
            }
          }
        })}
      />
    </>
  );
};
